import BaseService from "react-lib/apis/services/BaseService";

import { getPurRecord, PurOrderLine, PurReceiptLine, PurRecord } from "./purApi";
import { getPurDocument, listPurDocumentLines, normalizePurDocument, PurDocument, PurDocumentLine } from "./purDocumentsApi";
import { getPur, PUR_REQUEST_TIMEOUT_MS, PurClientConfig, purRequestOptions, readId, readObject, requireId } from "./purRead";

// Optional host capability for legacy callers/tests. Normal operation uses the
// authenticated runtime checks in preparePurDocumentVerification instead.
export interface PurDocumentVerificationAccess extends PurClientConfig {
  environmentVerified?: boolean;
  canVerifyDocuments: boolean;
}
export type PurDocumentVerificationAction = "BILL_FULL" | "COMPLETE" | "RECEIVE_FULL" | "RECEIVE_PARTIAL";
export interface PurDocumentVerificationPermission { resource: boolean; action: PurDocumentVerificationAction | null; actionGranted: boolean }
export interface PurDocumentVerificationAttempt {
  document: PurDocument; po: PurRecord; lines: PurDocumentLine[]; actorUserId: number;
  permission: PurDocumentVerificationPermission; expectedAction: PurDocumentVerificationAction | null;
}
export interface PurDocumentVerificationResult { document: PurDocument; po: PurRecord; verifiedByResponse: boolean }
export class PurDocumentVerificationError extends Error {
  constructor(public outcome: "blocked" | "unknown", public messageId: string, public permissionNames: string[] = []) {
    super(messageId); Object.setPrototypeOf(this, PurDocumentVerificationError.prototype);
  }
}
const block = (messageId = "MEERP_VERIFY_PREFLIGHT_ERROR", permissionNames: string[] = []): never => { throw new PurDocumentVerificationError("blocked", messageId, permissionNames); };
const pilotContext = (client: PurClientConfig): boolean => Boolean(client.apiToken?.trim())
  && readId(client.divisionId) !== null;

export const canVerifyPurDocuments = (client: PurClientConfig, access?: PurDocumentVerificationAccess): boolean => {
  if (!access) return pilotContext(client);
  return Boolean(client.apiToken?.trim())
    && client.apiToken === access.apiToken && readId(client.divisionId) !== null
    && readId(client.divisionId) === readId(access.divisionId)
    && purRequestOptions(client, "").url === purRequestOptions(access, "").url;
};
export const canVerifyPurDocumentType = (document: PurDocument): boolean =>
  ["inv", "tax_inv", "tax_inv_rcp"].includes(document.type);
export const purDocumentVerificationPermissionNames = (action: PurDocumentVerificationAction | null) => ({
  resource: "users.CREATE_PUR:document-verify", action: action ? `users.ACTION_PUR.PurchaseOrder:${action}` : null,
});
const permissionKeys = (action: PurDocumentVerificationAction | null) => ({
  resource: "perm_users_CREATE_PUR:document-verify", action: action ? `action_PUR_PurchaseOrder_${action}` : null,
});
export const checkPurDocumentVerificationPermissions = async (client: PurClientConfig, action: PurDocumentVerificationAction | null): Promise<PurDocumentVerificationPermission> => {
  if (!pilotContext(client)) return block("MEERP_VERIFY_SCOPE_ERROR");
  const keys = permissionKeys(action), body: Record<string, boolean> = { [keys.resource]: false };
  if (keys.action) body[keys.action] = false;
  const { url, headers } = purRequestOptions({ ...client, pathPrefix: "/users/apis" }, "user-permission/");
  const response = await new BaseService().client.post(url, body, { headers, timeout: PUR_REQUEST_TIMEOUT_MS });
  const data = readObject(response.data);
  return { resource: data[keys.resource] === true, action, actionGranted: action === null || data[keys.action!] === true };
};

const cents = (value: string | null, positive = false): number => {
  const match = /^(\d+)(?:\.(\d{1,2}))?$/.exec(value || "");
  if (!match) return block("MEERP_VERIFY_SOURCE_ERROR");
  const result = Number(`${match[1]}${(match[2] || "").padEnd(2, "0")}`);
  if (!Number.isSafeInteger(result) || (positive && result <= 0)) return block("MEERP_VERIFY_SOURCE_ERROR");
  return result;
};
const progress = (numerator: number, denominator: number): number => denominator <= 0 ? 0 : Math.min(10000, Math.round((numerator * 10000) / denominator));
const actionFor = (po: PurRecord, billed: number): PurDocumentVerificationAction | null => {
  const received = cents(po.percentReceived);
  if (received >= 10000 && billed >= 10000) return po.status === "COMPLETED" ? null : "COMPLETE";
  if (received >= 10000) return po.status === "TO_BILL" ? null : "RECEIVE_FULL";
  if (billed >= 10000) return po.status === "TO_RECEIVE" ? null : "BILL_FULL";
  return received > 0 && po.status !== "PARTIALLY_RECEIVED" ? "RECEIVE_PARTIAL" : null;
};
const statusFor = (before: string, action: PurDocumentVerificationAction | null): string => {
  if (action === "BILL_FULL") return "TO_RECEIVE";
  if (action === "COMPLETE") return "COMPLETED";
  if (action === "RECEIVE_FULL") return before === "TO_RECEIVE" ? "COMPLETED" : "TO_BILL";
  if (action === "RECEIVE_PARTIAL") return before === "TO_RECEIVE" ? "TO_RECEIVE" : "PARTIALLY_RECEIVED";
  return before;
};
const documentLines = async (client: PurClientConfig, document: PurDocument): Promise<PurDocumentLine[]> => {
  const lines: PurDocumentLine[] = []; let expected: number | null = null;
  for (let offset = 0; expected === null || lines.length < expected; offset += 100) {
    const page = await listPurDocumentLines(client, document.id, 100, offset);
    if (expected === null) expected = page.count;
    if (expected !== page.count || !page.records.length || lines.length + page.records.length > expected) return block("MEERP_VERIFY_SOURCE_ERROR");
    lines.push(...page.records);
  }
  if (expected !== lines.length || !lines.length) return block("MEERP_VERIFY_SOURCE_ERROR");
  return lines;
};
const facts = (document: PurDocument, po: PurRecord, lines: PurDocumentLine[]): { billed: number; action: PurDocumentVerificationAction | null } => {
  if (document.verified !== false || !canVerifyPurDocumentType(document) || document.poId !== po.id || document.projectId !== po.projectId
    || document.vendorId !== po.vendorId
    || !["APPROVED", "PARTIALLY_RECEIVED", "TO_BILL", "TO_RECEIVE"].includes(po.status) || !po.items.length || !lines.length) return block("MEERP_VERIFY_INELIGIBLE");
  const poLines = new Map<number, PurOrderLine>(); let oldBilled = 0, ordered = 0, invoiced = 0;
  for (const line of po.items) {
    if (line.kind !== "purchaseOrders" || line.id === null || line.poId !== po.id || line.productId === null || poLines.has(line.id)) return block("MEERP_VERIFY_SOURCE_ERROR");
    const order = cents(line.orderedQuantity, true), billed = cents(line.invoicedQuantity);
    if (billed > order) return block("MEERP_VERIFY_SOURCE_ERROR");
    poLines.set(line.id, line); ordered += order; invoiced += billed; oldBilled += cents(line.billedAmount);
  }
  const totals = new Map<number, { quantity: number; amount: number }>(), lineIds = new Set<number>();
  for (const line of lines) {
    if (line.id === null || lineIds.has(line.id) || line.documentId !== document.id || line.poItemId === null || line.productId === null || line.invoicedQuantity === null || line.lineTotal === null) return block("MEERP_VERIFY_SOURCE_ERROR");
    lineIds.add(line.id);
    const poLine = poLines.get(line.poItemId); if (!poLine || poLine.productId !== line.productId) return block("MEERP_VERIFY_SOURCE_ERROR");
    const total = totals.get(line.poItemId) || { quantity: 0, amount: 0 };
    total.quantity += cents(line.invoicedQuantity, true); total.amount += cents(line.lineTotal);
    if (!Number.isSafeInteger(total.quantity) || !Number.isSafeInteger(total.amount)) block("MEERP_VERIFY_SOURCE_ERROR");
    totals.set(line.poItemId, total);
  }
  totals.forEach((total, id) => { const line = poLines.get(id)!; if (cents(line.invoicedQuantity) + total.quantity > cents(line.orderedQuantity, true)) block("MEERP_VERIFY_INELIGIBLE"); });
  const plusQuantity = [...totals.values()].reduce((sum, item) => sum + item.quantity, 0), plusAmount = [...totals.values()].reduce((sum, item) => sum + item.amount, 0);
  const totalAmount = cents(po.totalAmount);
  const billed = totalAmount > 0 ? progress(oldBilled + plusAmount, totalAmount) : progress(invoiced + plusQuantity, ordered);
  return { billed, action: actionFor(po, billed) };
};
const receiptFacts = async (client: PurClientConfig, document: PurDocument, po: PurRecord, lines: PurDocumentLine[]): Promise<void> => {
  const linked = lines.filter((line) => line.receiptItemId !== null);
  if (!linked.length) return;
  if (document.receiptId === null) return block("MEERP_VERIFY_SOURCE_ERROR");
  const receipt = await getPurRecord(client, "receipts", document.receiptId);
  if (receipt.status !== "VERIFIED" || receipt.poId !== po.id || receipt.projectId !== po.projectId) return block("MEERP_VERIFY_SOURCE_ERROR");
  const totals = new Map<number, number>();
  linked.forEach((line) => totals.set(line.receiptItemId!, (totals.get(line.receiptItemId!) || 0) + cents(line.invoicedQuantity, true)));
  totals.forEach((invoiced, id) => {
    const invoiceLine = linked.find((line) => line.receiptItemId === id)!;
    const receiptLine = receipt.items.find((line): line is PurReceiptLine => line.kind === "receipts" && line.id === id);
    if (!receiptLine || receiptLine.receiptId !== receipt.id || receiptLine.poItemId !== invoiceLine.poItemId || receiptLine.productId !== invoiceLine.productId
      || cents(receiptLine.acceptedQuantity) < invoiced) block("MEERP_VERIFY_SOURCE_ERROR");
  });
};

export const preparePurDocumentVerification = async (client: PurClientConfig, id: number, access?: PurDocumentVerificationAccess): Promise<PurDocumentVerificationAttempt> => {
  if (!pilotContext(client) || (access && !canVerifyPurDocuments(client, access)) || readId(id) === null) return block("MEERP_VERIFY_SCOPE_ERROR");
  const document = await getPurDocument(client, id);
  if (document.id !== id || document.poId === null || document.projectId === null || document.vendorId === null) return block("MEERP_VERIFY_INELIGIBLE");
  const [po, lines] = await Promise.all([getPurRecord(client, "purchaseOrders", document.poId), documentLines(client, document)]);
  const result = facts(document, po, lines);
  await receiptFacts(client, document, po, lines);
  const [project, vendor, profile] = await Promise.all([getPur(client, `projects/${document.projectId}/`), getPur(client, `vendors/${document.vendorId}/`), getPur({ ...client, pathPrefix: "/users/apis" }, "users/profile/")]);
  const projectValue = readObject(project), vendorValue = readObject(vendor), actorUserId = readId(readObject(profile).user);
  if (actorUserId === null) return block("MEERP_VERIFY_IDENTITY_ERROR");
  if (readId(projectValue.id) !== document.projectId || readId(projectValue.division) !== readId(client.divisionId) || projectValue.active !== true
    || readId(vendorValue.id) !== document.vendorId) return block("MEERP_VERIFY_SCOPE_ERROR");
  const permission = await checkPurDocumentVerificationPermissions(client, result.action);
  if (!permission.resource || !permission.actionGranted) {
    const names = purDocumentVerificationPermissionNames(result.action);
    return block("MEERP_VERIFY_PERMISSION_MISSING", [...(!permission.resource ? [names.resource] : []), ...(!permission.actionGranted && names.action ? [names.action] : [])]);
  }
  return { document, po, lines, actorUserId, permission, expectedAction: result.action };
};

const postPurDocumentVerification = async (client: PurClientConfig, id: number): Promise<PurDocument> => {
  const documentId = requireId(id), { url, headers } = purRequestOptions(client, `documents/${documentId}/verify/`);
  const response = await new BaseService().client.post(url, {}, { headers, timeout: PUR_REQUEST_TIMEOUT_MS });
  const document = normalizePurDocument(response.data);
  if (document.id !== documentId || document.verified !== true) throw new Error("PUR verification result is unknown; read the document before any further action");
  return document;
};
// Compatibility only: normal runtime callers must use performPurDocumentVerification.
export const verifyPurDocument = async (client: PurClientConfig, id: number, access?: PurDocumentVerificationAccess): Promise<PurDocument> => {
  if (!access || !canVerifyPurDocuments(client, access)) throw new Error("PUR document verification is not enabled for this context");
  return postPurDocumentVerification(client, id);
};

const sameDocument = (before: PurDocument, after: PurDocument): boolean => ["id", "number", "date", "createdAt", "type", "vendorId", "vendorName", "projectId", "projectNumber", "poId", "receiptId", "grossAmount", "taxableAmount", "vatAmount", "exemptAmount", "withholdingAmount", "totalPriceVariance", "hasPriceVariance", "attachmentUrl"]
  .every((key) => before[key as keyof PurDocument] === after[key as keyof PurDocument]);
const sameLines = (before: PurDocumentLine[], after: PurDocumentLine[]): boolean => before.length === after.length
  && new Set(before.map((line) => line.id)).size === before.length && before.every((line) => {
    const current = after.find((item) => item.id === line.id);
    return Boolean(current) && ["id", "documentId", "poItemId", "receiptItemId", "productId", "productName", "invoicedQuantity", "unitCost", "taxableAmount", "vatAmount", "lineTotal", "priceVariance", "notes"]
      .every((key) => current![key as keyof PurDocumentLine] === line[key as keyof PurDocumentLine]);
  });
const factor = (value: string | null): number => {
  const match = /^(\d+)(?:\.(\d{1,4}))?$/.exec(value || "");
  if (!match) return block("MEERP_VERIFY_SOURCE_ERROR");
  const result = Number(`${match[1]}${(match[2] || "").padEnd(4, "0")}`);
  if (!Number.isSafeInteger(result) || result <= 0) return block("MEERP_VERIFY_SOURCE_ERROR");
  return result;
};
export const reconcilePurDocumentVerification = async (client: PurClientConfig, attempt: PurDocumentVerificationAttempt): Promise<{ document: PurDocument; po: PurRecord }> => {
  try {
  const [document, po] = await Promise.all([getPurDocument(client, attempt.document.id), getPurRecord(client, "purchaseOrders", attempt.po.id)]);
  const lines = await documentLines(client, document);
  if (document.id !== attempt.document.id || document.verified !== true || !sameDocument(attempt.document, document) || !sameLines(attempt.lines, lines)
    || po.id !== attempt.po.id || po.projectId !== attempt.po.projectId || po.vendorId !== attempt.po.vendorId) return block("MEERP_VERIFY_READBACK_ERROR");
  const previous = new Map(attempt.po.items.filter((line): line is PurOrderLine => line.kind === "purchaseOrders" && line.id !== null).map((line) => [line.id!, line]));
  const totals = new Map<number, { quantity: number; amount: number }>();
  attempt.lines.forEach((line) => { const total = totals.get(line.poItemId!) || { quantity: 0, amount: 0 }; total.quantity += cents(line.invoicedQuantity, true); total.amount += cents(line.lineTotal); totals.set(line.poItemId!, total); });
  if (po.items.length !== attempt.po.items.length || !po.items.every((line) => {
    if (line.kind !== "purchaseOrders" || line.id === null) return false;
    const before = previous.get(line.id), delta = totals.get(line.id) || { quantity: 0, amount: 0 };
    return Boolean(before) && line.poId === po.id && line.productId === before!.productId && line.unitId === before!.unitId
      && cents(line.orderedQuantity, true) === cents(before!.orderedQuantity, true) && cents(line.receivedQuantity) === cents(before!.receivedQuantity)
      && factor(line.conversionFactor) === factor(before!.conversionFactor) && cents(line.unitCost) === cents(before!.unitCost)
      && cents(line.invoicedQuantity) === cents(before!.invoicedQuantity) + delta.quantity && cents(line.billedAmount) === cents(before!.billedAmount) + delta.amount;
  })) return block("MEERP_VERIFY_READBACK_ERROR");
  const expected = facts({ ...attempt.document, verified: false }, attempt.po, attempt.lines);
  if (cents(po.percentBilled) !== expected.billed || cents(po.percentReceived) !== cents(attempt.po.percentReceived) || po.status !== statusFor(attempt.po.status, expected.action)) return block("MEERP_VERIFY_READBACK_ERROR");
  return { document, po };
  } catch { throw new PurDocumentVerificationError("unknown", "MEERP_VERIFY_READBACK_ERROR"); }
};

// CLI callers can use prepare -> user confirmation -> perform. perform refreshes
// every write-critical fact once more, submits exactly one empty-body POST, then
// requires document and PO-line/progress reconciliation before returning success.
export const performPurDocumentVerification = async (
  client: PurClientConfig, reviewed: PurDocumentVerificationAttempt, access?: PurDocumentVerificationAccess, isCurrent: () => boolean = () => true,
): Promise<PurDocumentVerificationResult> => {
  const fresh = await preparePurDocumentVerification(client, reviewed.document.id, access);
  if (!isCurrent() || fresh.actorUserId !== reviewed.actorUserId || JSON.stringify(fresh.document) !== JSON.stringify(reviewed.document)
    || JSON.stringify(fresh.po) !== JSON.stringify(reviewed.po) || JSON.stringify(fresh.lines) !== JSON.stringify(reviewed.lines)) return block("MEERP_VERIFY_CHANGED");
  let postStarted = false;
  try {
    postStarted = true;
    await postPurDocumentVerification(client, fresh.document.id);
    return { ...await reconcilePurDocumentVerification(client, fresh), verifiedByResponse: true };
  } catch (error) {
    if (!postStarted) throw error;
    // A transport error may have committed. Read once for authoritative
    // document + PO evidence, but never send a second POST.
    try { return { ...await reconcilePurDocumentVerification(client, fresh), verifiedByResponse: false }; }
    catch { throw new PurDocumentVerificationError("unknown", "MEERP_VERIFY_UNKNOWN"); }
  }
};
